import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import {
  loadBattleHistory,
  clearBattleHistory,
  getRecentEntries,
  calculateStats,
} from '../utils/battleHistoryManager';
import { BattleHistoryEntry } from '../types/BattleHistory';
import { getMonsterById } from '../constants/monsters';
import { BackgroundImageKey } from '../constants/imageKeys';

/** 戦績画面のレイアウト定数 */
const LAYOUT = {
  titleY: 35,
  summaryY: 80,
  summaryLineH: 28,
  monsterStatsY: 160,
  monsterLineH: 22,
  recentTitleY: 290,
  recentLineH: 20,
  recentStartY: 316,
  resetButtonY: 480,
  backButtonY: 515,
} as const;

/** 最近の履歴表示件数 */
const RECENT_COUNT = 5;

/**
 * 戦績画面シーン
 *
 * 総戦数・勝率、モンスター別勝敗数、最近10戦の履歴、リセットボタンを表示する。
 */
export class BattleStatsScene extends BaseScene {
  constructor() {
    super(SceneKey.BATTLE_STATS);
  }

  create(): void {
    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add
        .image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE)
        .setOrigin(0.5)
        .setAlpha(0.3);
    }

    this.createTitle();
    this.createSummary();
    this.createMonsterStats();
    this.createRecentHistory();
    this.createResetButton();
    this.createBackButton();
  }

  /** タイトル */
  private createTitle(): void {
    this.add
      .text(GAME_WIDTH / 2, LAYOUT.titleY, '戦績', {
        fontSize: '40px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  /** 総戦数・勝率サマリー */
  private createSummary(): void {
    const history = loadBattleHistory();
    const stats = calculateStats(history);

    const lines = [
      `総戦数: ${stats.totalCount}　　勝率: ${stats.winRate}%`,
      `${stats.winCount}勝 ${stats.loseCount}敗 ${stats.drawCount}分`,
    ];

    lines.forEach((line, i) => {
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.summaryY + i * LAYOUT.summaryLineH, line, {
          fontSize: '24px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5);
    });
  }

  /** モンスター別勝敗数 */
  private createMonsterStats(): void {
    const history = loadBattleHistory();
    const stats = calculateStats(history);
    const entries = Object.entries(stats.monsterStats);

    this.add
      .text(GAME_WIDTH / 2, LAYOUT.monsterStatsY - 30, 'モンスター別成績', {
        fontSize: '20px',
        color: '#aaddff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (entries.length === 0) {
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.monsterStatsY, '記録なし', {
          fontSize: '18px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5);
      return;
    }

    entries.forEach(([monsterId, mStat], i) => {
      const monster = getMonsterById(monsterId);
      const name = monster ? monster.name : monsterId;
      const line = `${name}: ${mStat.wins}勝 ${mStat.losses}敗 ${mStat.draws}分`;
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.monsterStatsY + i * LAYOUT.monsterLineH, line, {
          fontSize: '18px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5);
    });
  }

  /** 最近の10戦 */
  private createRecentHistory(): void {
    const history = loadBattleHistory();
    const recent = getRecentEntries(history, RECENT_COUNT);

    this.add
      .text(GAME_WIDTH / 2, LAYOUT.recentTitleY, `最近の${RECENT_COUNT}戦`, {
        fontSize: '20px',
        color: '#aaddff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (recent.length === 0) {
      this.add
        .text(GAME_WIDTH / 2, LAYOUT.recentStartY, '記録なし', {
          fontSize: '18px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5);
      return;
    }

    recent.forEach((entry, i) => {
      const y = LAYOUT.recentStartY + i * LAYOUT.recentLineH;
      this.add
        .text(GAME_WIDTH / 2, y, this.formatEntry(entry), {
          fontSize: '16px',
          color: this.getEntryColor(entry),
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5);
    });
  }

  /** 履歴エントリの表示テキストを生成 */
  private formatEntry(entry: BattleHistoryEntry): string {
    const resultLabel =
      entry.resultType === 'PLAYER1_WIN'
        ? '勝'
        : entry.resultType === 'PLAYER2_WIN'
          ? '負'
          : '分';
    const monster = getMonsterById(entry.playerMonsterId);
    const monsterName = monster ? monster.name : entry.playerMonsterId;
    const date = new Date(entry.date).toLocaleDateString('ja-JP', {
      month: 'numeric',
      day: 'numeric',
    });
    return `[${resultLabel}] ${monsterName} ${entry.turnCount}ターン（${date}）`;
  }

  /** 結果に応じた色を返す */
  private getEntryColor(entry: BattleHistoryEntry): string {
    if (entry.resultType === 'PLAYER1_WIN') return '#88ff88';
    if (entry.resultType === 'PLAYER2_WIN') return '#ff8888';
    return '#ffffff';
  }

  /** 全履歴リセットボタン */
  private createResetButton(): void {
    const text = this.add
      .text(GAME_WIDTH / 2, LAYOUT.resetButtonY, '戦績をリセット', {
        fontSize: '22px',
        color: '#ff6666',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    text.on('pointerover', () => text.setScale(1.1));
    text.on('pointerout', () => text.setScale(1.0));
    text.on('pointerdown', () => {
      clearBattleHistory();
      // 画面を再描画
      this.scene.restart();
    });
  }

  /** 戻るボタン */
  private createBackButton(): void {
    const text = this.add
      .text(GAME_WIDTH / 2, LAYOUT.backButtonY, '戻る', {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    text.on('pointerover', () => {
      text.setColor('#ffffff');
      text.setScale(1.1);
    });
    text.on('pointerout', () => {
      text.setColor('#ffffff');
      text.setScale(1.0);
    });
    text.on('pointerdown', () => {
      this.transitionTo(SceneKey.TITLE);
    });
  }
}
